import Vue from "vue";
import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";

import { ApiFilterValue } from "@/modules/api/api-filter-value.type";
import { QueryOrderParameter } from "@/modules/api/query-order-parameter";
import { DIContainer } from "@/app.container";
import { Dictionary } from "@/lib/Dictionary.type";
import { DirectoryValue } from "@/modules/api/directory-value.model";
import dataStore from "@/store";
import { ResourceCollection } from "@/modules/api/resource.collection";

import Factory from "../factory.model";
import FactoryPhaseSupport from "../factory-phase-support.model";
import FactorySettings from "../factory-settings.model";
import FactoryProductDepositSetting from "../factory-product-deposit-setting.model";
import FactoryAssignment from "../plushie-factory-asignment.model";
import UserFactoryRelation from "../user-factory-relation.model";
import ProductionTimeNorm from "../production-time-norm.model";
import ProductionTimeNormHistoryItem from "../production-time-norm-history-item.model";
import ProductAllocation from "../product-allocation.model";
import UpgradeAllocation from "../upgrade-allocation.model";
import FactoryInvoicingSettings from "../factory-invoicing-settings.model";
import { ProductAllocationSlotTypeValue } from "../product-allication-slot-type.value";

const name = "FactoryStore";
const allocationFetchLimit = 99999;
let phaseSupportRequestGeneration = 0;

if ((dataStore.state as any)[name]) {
  dataStore.unregisterModule(name);
}

const getDefaultState = () => {
  return {
    factory: {},
    language: {},
    factoryAssignment: {},
    factoryProductionTimeNorms: {},
    factoryProductionTimeNormHistoryItems: {},
    historyType: {},
    plushie: {},
    productAllocation: {},
    upgradeAllocation: {},
    productFactoriesRelation: {
      [ProductAllocationSlotTypeValue.PRODUCTION]: {},
      [ProductAllocationSlotTypeValue.DESIGN]: {},
    },
    factoryPhaseSupport: {},
    factorySettings: {},
    factoryPhaseSupportByFactory: {},
    productionTimeNorm: {},
    productionTimeNormHistoryItem: {},
    userFactoryRelation: {},
    userRelations: {},
    factoryRelations: {},
    allFactoriesLoaded: false,
    factoryInvoicingSettings: {},
    productDepositSetting: {},
  };
};

interface PlushieRelations {
  factoryAssignment: string;
}

interface ProductAllocationCollection {
  slotTypes: ProductAllocationSlotTypeValue[];
  items: ProductAllocation[];
}

type FactoryPhaseSupportSlotIndex = Partial<
  Record<ProductAllocationSlotTypeValue, string>
>;

@Module({ name, dynamic: true, store: dataStore })
export default class FactoryStore extends VuexModule {
  factory: Dictionary<Factory> = {};
  language: Dictionary<DirectoryValue> = {};
  factoryAssignment: Dictionary<FactoryAssignment> = {};
  factoryProductionTimeNorms: Dictionary<string[]> = {};
  factoryProductionTimeNormHistoryItems: Dictionary<string[]> = {};
  historyType: Dictionary<DirectoryValue> = {};
  plushie: Dictionary<PlushieRelations> = {};
  productAllocation: Dictionary<ProductAllocation> = {};
  productFactoriesRelation: Record<
    ProductAllocationSlotTypeValue,
    Dictionary<string[]>
  > = {
    [ProductAllocationSlotTypeValue.PRODUCTION]: {},
    [ProductAllocationSlotTypeValue.DESIGN]: {},
  };

  factoryPhaseSupport: Dictionary<FactoryPhaseSupport> = {};
  factorySettings: Dictionary<FactorySettings> = {};
  factoryPhaseSupportByFactory: Dictionary<FactoryPhaseSupportSlotIndex> = {};

  productionTimeNorm: Dictionary<ProductionTimeNorm> = {};
  productionTimeNormHistoryItem: Dictionary<ProductionTimeNormHistoryItem> = {};
  upgradeAllocation: Dictionary<UpgradeAllocation> = {};
  userFactoryRelation: Dictionary<UserFactoryRelation> = {};
  userRelations: Dictionary<string> = {};
  factoryRelations: Dictionary<string[]> = {};
  factoryInvoicingSettings: Dictionary<FactoryInvoicingSettings> = {};
  productDepositSetting: Dictionary<FactoryProductDepositSetting> = {};

  get getProductDepositSettingsByFactoryId(): (
    factoryId: string
  ) => FactoryProductDepositSetting[] {
    return (factoryId: string) =>
      Object.values(this.productDepositSetting).filter(
        (item) => item.factoryId === factoryId
      );
  }

  @Mutation
  replaceProductDepositSettings({
    factoryId,
    items,
  }: {
    factoryId: string;
    items: FactoryProductDepositSetting[];
  }): void {
    Object.values(this.productDepositSetting).forEach((item) => {
      if (item.factoryId === factoryId) {
        Vue.delete(this.productDepositSetting, item.id);
      }
    });
    items.forEach((item) => Vue.set(this.productDepositSetting, item.id, item));
  }

  @Action({ rawError: true })
  async loadProductDepositSettingsByFactoryId({
    factoryId,
  }: {
    factoryId: string;
  }): Promise<FactoryProductDepositSetting[]> {
    const collection = await DIContainer.FactoryProductDepositSettingRepository.getByFactoryId(
      factoryId,
      true,
      false
    );
    const items = collection.getItems();
    this.replaceProductDepositSettings({ factoryId, items });
    return items;
  }

  @Action({ rawError: true })
  async saveProductDepositSettingsForFactory({
    factoryId,
    items,
  }: {
    factoryId: string;
    items: FactoryProductDepositSetting[];
  }): Promise<FactoryProductDepositSetting[]> {
    const saved = await DIContainer.FactoryProductDepositSettingRepository.updateForFactory(
      factoryId,
      items
    );
    this.replaceProductDepositSettings({ factoryId, items: saved });
    return saved;
  }

  allFactoriesLoaded = false;

  get getFactorySettingsById(): (
    factoryId: string
  ) => FactorySettings | undefined {
    return (factoryId: string) => this.factorySettings[factoryId];
  }

  @Mutation
  updateFactorySettings(settings: FactorySettings): void {
    Vue.set(this.factorySettings, settings.factoryId, settings);
  }

  @Mutation
  clearFactorySettings(factoryId: string): void {
    Vue.delete(this.factorySettings, factoryId);
  }

  @Action({ rawError: true })
  async loadFactorySettings({
    factoryId,
    useCache = true,
  }: {
    factoryId: string;
    useCache?: boolean;
  }): Promise<FactorySettings | undefined> {
    if (useCache && this.factorySettings[factoryId]) {
      return this.factorySettings[factoryId];
    }

    try {
      const settings = await DIContainer.FactorySettingsRepository.getById(
        factoryId,
        false
      );

      if (settings) {
        this.updateFactorySettings(settings);
      } else {
        this.clearFactorySettings(factoryId);
      }

      return settings;
    } catch (error) {
      this.clearFactorySettings(factoryId);

      throw error;
    }
  }

  @Action({ rawError: true })
  async saveFactorySettings(
    settings: FactorySettings
  ): Promise<FactorySettings> {
    const saved = await DIContainer.FactorySettingsRepository.save(settings);

    this.updateFactorySettings(saved);

    return saved;
  }

  // ################################### FACTORIES #########################################

  get factoriesList(): Factory[] {
    const list: Factory[] = [];

    Object.keys(this.factory).forEach((id) => {
      list.push(this.factory[id]);
    });

    list.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

    return list;
  }

  get activeFactoriesList(): Factory[] {
    const list: Factory[] = [];

    Object.keys(this.factory).forEach((id) => {
      if (!this.factory[id].isActive) {
        return;
      }

      list.push(this.factory[id]);
    });

    list.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

    return list;
  }

  get getFactoryById(): (id: string) => Factory | undefined {
    return (id: string) => this.factory[id];
  }

  @Mutation
  updateAllFactoriesLoaded(value: boolean): void {
    this.allFactoriesLoaded = value == true;
  }

  @Mutation
  updateFactory(payload: Factory): void {
    Vue.set(this.factory, payload.id, payload);
  }

  @Action({ rawError: true })
  async loadFactories(): Promise<Factory[]> {
    let items: Factory[];

    if (!this.allFactoriesLoaded) {
      const collection = await DIContainer.FactoryRepository.getList(1, 999);
      items = collection.getItems();

      items.forEach((item) => {
        this.updateFactory(item);
      });

      this.updateAllFactoriesLoaded(true);
    }

    items = [];

    Object.keys(this.factory).forEach((key) => {
      items.push(this.factory[key]);
    });

    return items;
  }

  @Action({ rawError: true })
  async loadFactoriesWithFilter({
    filter,
    order,
    useCache = true,
  }: {
    filter?: Dictionary<ApiFilterValue>;
    order?: QueryOrderParameter;
    useCache?: boolean;
  }): Promise<ResourceCollection<Factory>> {
    const collection = await DIContainer.FactoryRepository.getList(
      1,
      999,
      filter,
      order,
      useCache
    );

    collection.getItems().forEach((item) => {
      this.updateFactory(item);
    });

    return collection;
  }

  @Action({ rawError: true })
  async loadFactoryById({
    id,
    useCache = true,
  }: {
    id: string;
    useCache?: boolean;
  }): Promise<Factory | undefined> {
    if (useCache && this.factory[id] !== undefined) {
      return this.factory[id];
    }

    const factory = await DIContainer.FactoryRepository.getById(id, useCache);

    if (factory) {
      this.updateFactory(factory);
    }

    return factory;
  }

  @Action({ rawError: true })
  async saveFactory(factory: Factory): Promise<Factory> {
    const item = await DIContainer.FactoryRepository.save(factory);

    this.updateFactory(item);

    return item;
  }

  // ################################### HISTORY TYPES #########################################
  get getHistoryTypeById(): (id: string) => DirectoryValue | undefined {
    return (id: string) => this.historyType[id];
  }

  @Mutation
  updateHistoryType(payload: DirectoryValue): void {
    Vue.set(this.historyType, payload.id, payload);
  }

  @Action({ rawError: true })
  async loadHistoryTypes(): Promise<DirectoryValue[]> {
    let items: DirectoryValue[];

    if (Object.keys(this.historyType).length === 0) {
      const collection = await DIContainer.HistoryTypeRepository.getList();
      items = collection.getItems();

      items.forEach((item) => {
        this.updateHistoryType(item);
      });
    }

    items = [];

    Object.keys(this.historyType).forEach((key) => {
      items.push(this.historyType[key]);
    });

    return items;
  }

  // ################################### LANGUAGES #########################################

  get languages(): DirectoryValue[] {
    const list: DirectoryValue[] = [];

    Object.keys(this.language).forEach((id) => {
      list.push(this.language[id]);
    });

    list.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

    return list;
  }

  get languagesDictionary(): Dictionary<DirectoryValue> {
    return { ...this.language };
  }

  get getLanguageById(): (id: string) => DirectoryValue | undefined {
    return (id: string) => this.language[id];
  }

  @Mutation
  updateLanguage(payload: DirectoryValue): void {
    Vue.set(this.language, payload.id, payload);
  }

  @Action({ rawError: true })
  async loadLanguages(): Promise<DirectoryValue[]> {
    let items: DirectoryValue[];

    if (Object.keys(this.language).length === 0) {
      const collection = await DIContainer.LanguageRepository.getList();
      items = collection.getItems();

      items.forEach((item) => {
        this.updateLanguage(item);
      });
    }

    items = [];

    Object.keys(this.language).forEach((key) => {
      items.push(this.language[key]);
    });

    return items;
  }

  @Action({ rawError: true })
  async loadLanguageById(id: string): Promise<DirectoryValue | undefined> {
    await this.loadLanguages();

    return this.getLanguageById(id);
  }

  // ################################### FACTORY ASSIGNMENTS #########################################

  get getFactoryAssignmentByPlushieId(): (
    plushieId: string
  ) => FactoryAssignment | undefined {
    return (plushieId: string) => {
      if (this.plushie[plushieId] == null) {
        return undefined;
      }

      const id = this.plushie[plushieId].factoryAssignment;

      if (id == null) {
        return undefined;
      }

      return this.factoryAssignment[id];
    };
  }

  @Mutation
  updateFactoryAssignment(payload: FactoryAssignment): void {
    Vue.set(this.factoryAssignment, payload.id, payload);
    this.plushie[payload.plushie] = { factoryAssignment: payload.id };
  }

  @Action({ rawError: true })
  async loadFactoryAssignmentByPlushieId({
    plushieId,
    useCache = true,
  }: {
    plushieId: string;
    useCache?: boolean;
  }): Promise<FactoryAssignment | undefined> {
    let assignmentId;

    if (useCache && this.plushie[plushieId] != null) {
      assignmentId = this.plushie[plushieId].factoryAssignment;
    }

    if (assignmentId != null && this.factoryAssignment[assignmentId] != null) {
      return this.factoryAssignment[assignmentId];
    }

    const factoryAssignment = await DIContainer.PlushieFactoryAssignmentRepository.getByPlushieId(
      plushieId
    );

    if (factoryAssignment) {
      this.updateFactoryAssignment(factoryAssignment);
    }

    return factoryAssignment;
  }

  @Action({ rawError: true })
  async loadFactoryAssignmentsByPlushieIds({
    plushieIds,
    useCache = true,
  }: {
    plushieIds: string[];
    useCache?: boolean;
  }): Promise<Dictionary<FactoryAssignment>> {
    const missing: string[] = [];
    const result: Dictionary<FactoryAssignment> = {};

    plushieIds.forEach((id) => {
      if (useCache && this.plushie[id] != null) {
        const assignmentId = this.plushie[id].factoryAssignment;

        if (!assignmentId || !this.factoryAssignment[assignmentId]) {
          missing.push(id);
          return;
        }

        result[id] = this.factoryAssignment[assignmentId];
        return;
      }

      missing.push(id);
    });

    if (!missing.length) {
      return result;
    }

    const items = await DIContainer.PlushieFactoryAssignmentRepository.getByPlushieIds(
      missing
    );

    Object.keys(items).forEach((itemId) => {
      const assignment = items[itemId];

      if (assignment === undefined) {
        return;
      }

      this.updateFactoryAssignment(assignment);

      result[assignment.plushie] = assignment;
    });

    return result;
  }

  // ################################### PRODUCT ALLOCATIONS #########################################
  get getFactoriesIdsByProductIdAndSlot(): (
    productId: string,
    slotType: ProductAllocationSlotTypeValue
  ) => string[] {
    return (productId: string, slotType: ProductAllocationSlotTypeValue) => {
      const slotFactories = this.productFactoriesRelation[slotType][productId];

      return slotFactories ? Array.from(slotFactories) : [];
    };
  }

  get getProductAllocationsBySlotType(): (
    slotType: ProductAllocationSlotTypeValue
  ) => ProductAllocation[] {
    return (slotType: ProductAllocationSlotTypeValue) =>
      Object.values(this.productAllocation).filter(
        (item) => item.slotType === slotType
      );
  }

  @Mutation
  replaceProductAllocations(payload: ProductAllocationCollection): void {
    const slotTypes = Array.from(new Set(payload.slotTypes));
    const productAllocation: Dictionary<ProductAllocation> = {};
    const productFactoriesRelation: Record<
      ProductAllocationSlotTypeValue,
      Dictionary<string[]>
    > = {
      [ProductAllocationSlotTypeValue.PRODUCTION]: slotTypes.includes(
        ProductAllocationSlotTypeValue.PRODUCTION
      )
        ? {}
        : {
            ...this.productFactoriesRelation[
              ProductAllocationSlotTypeValue.PRODUCTION
            ],
          },
      [ProductAllocationSlotTypeValue.DESIGN]: slotTypes.includes(
        ProductAllocationSlotTypeValue.DESIGN
      )
        ? {}
        : {
            ...this.productFactoriesRelation[
              ProductAllocationSlotTypeValue.DESIGN
            ],
          },
    };

    Object.keys(this.productAllocation).forEach((id) => {
      const allocation = this.productAllocation[id];
      if (!slotTypes.includes(allocation.slotType)) {
        productAllocation[id] = allocation;
      }
    });

    payload.items.forEach((allocation) => {
      productAllocation[allocation.id] = allocation;

      if (allocation.distributionPercent <= 0) {
        return;
      }

      const factories =
        productFactoriesRelation[allocation.slotType][allocation.product] || [];
      if (!factories.includes(allocation.factory)) {
        factories.push(allocation.factory);
      }
      productFactoriesRelation[allocation.slotType][
        allocation.product
      ] = factories;
    });

    this.productAllocation = productAllocation;
    this.productFactoriesRelation = productFactoriesRelation;
  }

  @Action({ rawError: true })
  async loadProductAllocations(
    slotTypes: ProductAllocationSlotTypeValue[]
  ): Promise<ProductAllocation[]> {
    if (!slotTypes.length) {
      return [];
    }

    const collection = await DIContainer.ProductAllocationRepository.getList(
      1,
      allocationFetchLimit,
      { slotType: slotTypes },
      undefined,
      false
    );

    const items = collection.getItems();
    this.replaceProductAllocations({ slotTypes, items });

    return items;
  }

  @Action({ rawError: true })
  async saveProductAllocations(
    productAllocations: ProductAllocation[]
  ): Promise<ProductAllocation[]> {
    const collection = await DIContainer.ProductAllocationRepository.saveItems(
      productAllocations
    );

    const items = collection.getItems();

    const slotTypes = Array.from(
      new Set(productAllocations.map((item) => item.slotType))
    );
    this.replaceProductAllocations({ slotTypes, items });

    return items;
  }

  // ################################### FACTORY PHASE SUPPORT #########################################
  get getFactoryPhaseSupportByFactoryIdAndSlotType(): (
    factoryId: string,
    slotType: ProductAllocationSlotTypeValue
  ) => FactoryPhaseSupport | undefined {
    return (factoryId: string, slotType: ProductAllocationSlotTypeValue) => {
      const slotIndex = this.factoryPhaseSupportByFactory[factoryId];

      if (!slotIndex) {
        return undefined;
      }

      const supportId = slotIndex[slotType];

      if (!supportId) {
        return undefined;
      }

      return this.factoryPhaseSupport[supportId];
    };
  }

  get getIsFactoryPhaseSupported(): (
    factoryId: string,
    slotType: ProductAllocationSlotTypeValue
  ) => boolean {
    return (factoryId: string, slotType: ProductAllocationSlotTypeValue) =>
      this.getFactoryPhaseSupportByFactoryIdAndSlotType(factoryId, slotType) !==
      undefined;
  }

  @Mutation
  updateFactoryPhaseSupport(payload: FactoryPhaseSupport): void {
    Vue.set(this.factoryPhaseSupport, payload.id, payload);

    if (!this.factoryPhaseSupportByFactory[payload.factory]) {
      Vue.set(this.factoryPhaseSupportByFactory, payload.factory, {});
    }

    Vue.set(
      this.factoryPhaseSupportByFactory[payload.factory],
      payload.slotType,
      payload.id
    );
  }

  @Mutation
  deleteFactoryPhaseSupportFromStore(payload: FactoryPhaseSupport): void {
    Vue.delete(this.factoryPhaseSupport, payload.id);

    const slotIndex = this.factoryPhaseSupportByFactory[payload.factory];

    if (!slotIndex || slotIndex[payload.slotType] !== payload.id) {
      return;
    }

    Vue.delete(slotIndex, payload.slotType);
  }

  @Mutation
  clearFactoryPhaseSupportsForFactory(factoryId: string): void {
    const slotIndex = this.factoryPhaseSupportByFactory[factoryId];

    if (slotIndex) {
      Object.values(slotIndex).forEach((supportId) => {
        if (supportId) {
          Vue.delete(this.factoryPhaseSupport, supportId);
        }
      });
    }

    Vue.set(this.factoryPhaseSupportByFactory, factoryId, {});
  }

  @Mutation
  replaceFactoryPhaseSupportSnapshot(payload: {
    factoryIds: string[];
    items: FactoryPhaseSupport[];
  }): void {
    const requested = new Set(payload.factoryIds);
    const records: Dictionary<FactoryPhaseSupport> = {};
    const index: Dictionary<FactoryPhaseSupportSlotIndex> = {
      ...this.factoryPhaseSupportByFactory,
    };
    Object.values(this.factoryPhaseSupport).forEach((item) => {
      if (!requested.has(item.factory)) records[item.id] = item;
    });
    payload.factoryIds.forEach((id) => { index[id] = {}; });
    payload.items.forEach((item) => {
      records[item.id] = item;
      index[item.factory][item.slotType] = item.id;
    });
    this.factoryPhaseSupport = records;
    this.factoryPhaseSupportByFactory = index;
  }

  @Action({ rawError: true })
  async loadCompleteFactoryPhaseSupports({
    factoryIds,
    isCurrent,
  }: {
    factoryIds: string[];
    isCurrent?: () => boolean;
  }): Promise<FactoryPhaseSupport[]> {
    const generation = ++phaseSupportRequestGeneration;
    const items = await DIContainer.FactoryPhaseSupportRepository.getCompleteByFactoryIds(factoryIds);
    if (generation !== phaseSupportRequestGeneration || (isCurrent && !isCurrent())) {
      throw new Error("Factory phase support request superseded");
    }
    this.replaceFactoryPhaseSupportSnapshot({ factoryIds, items });
    return items;
  }

  @Action({ rawError: true })
  async loadFactoryPhaseSupportsByFactoryId({
    factoryId,
    useCache = true,
  }: {
    factoryId: string;
    useCache?: boolean;
  }): Promise<FactoryPhaseSupport[]> {
    if (useCache && this.factoryPhaseSupportByFactory[factoryId]) {
      const slotIndex = this.factoryPhaseSupportByFactory[factoryId];

      return Object.values(slotIndex)
        .map((supportId) =>
          supportId ? this.factoryPhaseSupport[supportId] : undefined
        )
        .filter(
          (support): support is FactoryPhaseSupport => support !== undefined
        );
    }

    const collection = await DIContainer.FactoryPhaseSupportRepository.getByFactoryId(
      factoryId,
      useCache
    );

    const items = collection.getItems();

    this.clearFactoryPhaseSupportsForFactory(factoryId);

    items.forEach((item) => {
      this.updateFactoryPhaseSupport(item);
    });

    return items;
  }

  @Action({ rawError: true })
  async enableFactoryPhaseSupport(
    phaseSupport: FactoryPhaseSupport
  ): Promise<FactoryPhaseSupport> {
    const existingSupport = this.getFactoryPhaseSupportByFactoryIdAndSlotType(
      phaseSupport.factory,
      phaseSupport.slotType
    );

    if (existingSupport) {
      return existingSupport;
    }

    const support = await DIContainer.FactoryPhaseSupportRepository.create(
      phaseSupport
    );

    this.updateFactoryPhaseSupport(support);

    return support;
  }

  @Action({ rawError: true })
  async disableFactoryPhaseSupport({
    factoryId,
    slotType,
  }: {
    factoryId: string;
    slotType: ProductAllocationSlotTypeValue;
  }): Promise<void> {
    const support = this.getFactoryPhaseSupportByFactoryIdAndSlotType(
      factoryId,
      slotType
    );

    if (!support) {
      return;
    }

    await DIContainer.FactoryPhaseSupportRepository.deleteById(support.id);

    this.deleteFactoryPhaseSupportFromStore(support);
  }

  // ################################### PRODUCTION TIME NORMS #########################################
  get getProductionTimeNormById(): (
    id: string
  ) => ProductionTimeNorm | undefined {
    return (id: string) => this.productionTimeNorm[id];
  }

  get getProductionTimeNormsByFactoryId(): (
    factoryId: string
  ) => ProductionTimeNorm[] {
    return (factoryId: string) => {
      if (this.factoryProductionTimeNorms[factoryId] == null) {
        return [];
      }

      const ids = this.factoryProductionTimeNorms[factoryId];

      const result: ProductionTimeNorm[] = [];

      ids.forEach((id) => {
        result.push(this.productionTimeNorm[id]);
      });

      return result;
    };
  }

  @Mutation
  updateFactoryProductionTimeNorms({
    factoryId,
    productionTimeNorms,
  }: {
    factoryId: string;
    productionTimeNorms: ProductionTimeNorm[];
  }): void {
    productionTimeNorms.forEach((timeNorm) => {
      if (timeNorm.factory !== factoryId) {
        throw new Error(
          "All production time norms should belong to the specified factory!"
        );
      }
    });

    const productionTimeNormIds: string[] = [];

    productionTimeNorms.forEach((timeNorm) => {
      productionTimeNormIds.push(timeNorm.id);
      Vue.set(this.productionTimeNorm, timeNorm.id, timeNorm);
    });

    Vue.set(this.factoryProductionTimeNorms, factoryId, productionTimeNormIds);
  }

  @Action({ rawError: true })
  async loadProductionTimeNormsByFactoryId({
    factoryId,
    useCache = true,
    isDisablePagination = false,
  }: {
    factoryId: string;
    useCache?: boolean;
    isDisablePagination?: boolean;
  }): Promise<ProductionTimeNorm[]> {
    if (useCache && this.factoryProductionTimeNorms[factoryId]) {
      const ids = this.factoryProductionTimeNorms[factoryId];

      const result: ProductionTimeNorm[] = [];

      ids.forEach((id) => {
        result.push(this.productionTimeNorm[id]);
      });

      return result;
    }

    const collection = await DIContainer.ProductionTimeNormRepository.getByFactoryId(
      factoryId,
      isDisablePagination
    );

    const items = collection.getItems();

    this.updateFactoryProductionTimeNorms({
      factoryId,
      productionTimeNorms: items,
    });

    return items;
  }

  @Action({ rawError: true })
  async saveProductionTimeNormsForFactory({
    factoryId,
    values,
  }: {
    factoryId: string;
    values: ProductionTimeNorm[];
  }): Promise<ProductionTimeNorm[]> {
    const items = await DIContainer.ProductionTimeNormRepository.updateProductionTimeNormsForFactory(
      factoryId,
      values
    );

    this.updateFactoryProductionTimeNorms({
      factoryId,
      productionTimeNorms: items,
    });

    return items;
  }

  // ################################### PRODUCTION TIME NORM HISTORY ITEMS #########################################
  get getProductionTimeNormHistoryItemById(): (
    id: string
  ) => ProductionTimeNormHistoryItem | undefined {
    return (id: string) => this.productionTimeNormHistoryItem[id];
  }

  get getProductionTimeNormItemsByFactoryId(): (
    factoryId: string
  ) => ProductionTimeNormHistoryItem[] {
    return (factoryId: string) => {
      if (this.factoryProductionTimeNormHistoryItems[factoryId] == null) {
        return [];
      }

      const ids = this.factoryProductionTimeNormHistoryItems[factoryId];

      const result: ProductionTimeNormHistoryItem[] = [];

      ids.forEach((id) => {
        result.push(this.productionTimeNormHistoryItem[id]);
      });

      return result;
    };
  }

  @Mutation
  updateFactoryProductionTimeNormHistoryItems({
    factoryId,
    productionTimeNormHistoryItems,
  }: {
    factoryId: string;
    productionTimeNormHistoryItems: ProductionTimeNormHistoryItem[];
  }): void {
    productionTimeNormHistoryItems.forEach((historyItem) => {
      if (historyItem.factory !== factoryId) {
        throw new Error(
          "All production time norm history items should belong to the specified factory!"
        );
      }
    });

    const productionTimeNormHistoryItemIds: string[] = [];

    productionTimeNormHistoryItems.forEach((historyItem) => {
      productionTimeNormHistoryItemIds.push(historyItem.id);
      Vue.set(this.productionTimeNormHistoryItem, historyItem.id, historyItem);
    });

    Vue.set(
      this.factoryProductionTimeNormHistoryItems,
      factoryId,
      productionTimeNormHistoryItemIds
    );
  }

  @Action({ rawError: true })
  async loadProductionTimeNormHistoryItemsByFactoryId({
    factoryId,
    useCache = true,
  }: {
    factoryId: string;
    useCache?: boolean;
  }): Promise<ProductionTimeNormHistoryItem[]> {
    if (useCache && this.factoryProductionTimeNormHistoryItems[factoryId]) {
      const ids = this.factoryProductionTimeNormHistoryItems[factoryId];

      const result: ProductionTimeNormHistoryItem[] = [];

      ids.forEach((id) => {
        result.push(this.productionTimeNormHistoryItem[id]);
      });

      return result;
    }

    const collection = await DIContainer.ProductionTimeNormHistoryItemRepository.getByFactoryId(
      factoryId
    );

    const items = collection.getItems();

    this.updateFactoryProductionTimeNormHistoryItems({
      factoryId,
      productionTimeNormHistoryItems: items,
    });

    return items;
  }

  // ################################### UPGRADE ALLOCATION #########################################
  @Mutation
  replaceUpgradeAllocations(items: UpgradeAllocation[]): void {
    const upgradeAllocation: Dictionary<UpgradeAllocation> = {};

    items.forEach((item) => {
      upgradeAllocation[item.id] = item;
    });

    this.upgradeAllocation = upgradeAllocation;
  }

  @Action({ rawError: true })
  async loadUpgradeAllocations(): Promise<UpgradeAllocation[]> {
    const collection = await DIContainer.UpgradeAllocationRepository.getList(
      1,
      allocationFetchLimit,
      undefined,
      undefined,
      false
    );

    const items = collection.getItems();
    this.replaceUpgradeAllocations(items);

    return items;
  }

  @Action({ rawError: true })
  async saveUpgradeAllocations(
    upgradeAllocations: UpgradeAllocation[]
  ): Promise<UpgradeAllocation[]> {
    const collection = await DIContainer.UpgradeAllocationRepository.saveItems(
      upgradeAllocations
    );

    const items = collection.getItems();

    this.replaceUpgradeAllocations(items);

    return items;
  }

  // ################################### USER FACTORY RELATIONS #########################################
  get getUserFactoryRelationByUserId(): (
    userId: string
  ) => UserFactoryRelation | undefined {
    return (userId: string) => {
      const relationId = this.userRelations[userId];

      return this.userFactoryRelation[relationId];
    };
  }

  get getUserFactoryRelationsByFactoryId(): (
    factoryId: string
  ) => UserFactoryRelation[] {
    return (factoryId: string) => {
      const relationIds = this.factoryRelations[factoryId] || [];

      return relationIds.map(
        (relationId) => this.userFactoryRelation[relationId]
      );
    };
  }

  @Mutation
  clearFactoryRelationsForFactory(factoryId: string): void {
    Vue.set(this.factoryRelations, factoryId, []);
  }

  @Mutation
  updateUserFactoryRelation(payload: UserFactoryRelation): void {
    Vue.set(this.userFactoryRelation, payload.id, payload);
    Vue.set(this.userRelations, payload.user, payload.id);

    if (!this.factoryRelations[payload.factory]) {
      Vue.set(this.factoryRelations, payload.factory, []);
    }

    if (this.factoryRelations[payload.factory].includes(payload.id)) {
      return;
    }

    this.factoryRelations[payload.factory].push(payload.id);
  }

  @Mutation
  deleteUserFactoryRelationFromStore(relation: UserFactoryRelation): void {
    Vue.delete(this.userFactoryRelation, relation.id);
    Vue.delete(this.userRelations, relation.user);

    if (!this.factoryRelations[relation.factory]) {
      return;
    }

    const index = this.factoryRelations[relation.factory].findIndex(
      (relationId) => relation.id === relationId
    );

    if (index < 0) {
      return;
    }

    this.factoryRelations[relation.factory].splice(index, 1);
  }

  @Action({ rawError: true })
  async loadUserFactoryRelationByUserId({
    userId,
    useCache = true,
  }: {
    userId: string;
    useCache?: boolean;
  }): Promise<UserFactoryRelation | undefined> {
    if (useCache && this.userRelations[userId]) {
      return this.getUserFactoryRelationByUserId(userId);
    }

    const item = await DIContainer.UserFactoryRelationRepository.getByUserId(
      userId
    );

    if (!item) {
      return;
    }

    this.updateUserFactoryRelation(item);

    return item;
  }

  @Action({ rawError: true })
  async loadUserFactoryRelationByUserIds({
    userIds,
    useCache = true,
  }: {
    userIds: string[];
    useCache?: boolean;
  }): Promise<Dictionary<UserFactoryRelation>> {
    const missing: string[] = [];
    const result: Dictionary<UserFactoryRelation> = {};

    userIds.forEach((id) => {
      if (useCache && this.userRelations[id]) {
        const relation = this.getUserFactoryRelationByUserId(id);
        if (relation) {
          result[id] = relation;
          return;
        }
      }

      missing.push(id);
    });

    if (!missing.length) {
      return result;
    }

    const items = await DIContainer.UserFactoryRelationRepository.getByUserIds(
      missing
    );

    items.forEach((item) => {
      this.updateUserFactoryRelation(item);
      result[item.user] = item;
    });

    return result;
  }

  @Action({ rawError: true })
  async loadUserFactoryRelationsByFactoryId(
    factoryId: string
  ): Promise<UserFactoryRelation[]> {
    const items = await DIContainer.UserFactoryRelationRepository.getByFactoryId(
      factoryId
    );

    this.clearFactoryRelationsForFactory(factoryId);

    items.forEach((item) => {
      this.updateUserFactoryRelation(item);
    });

    return items;
  }

  @Action({ rawError: true })
  async saveUserFactoryRelation(
    relation: UserFactoryRelation
  ): Promise<UserFactoryRelation> {
    await DIContainer.UserFactoryRelationRepository.save(relation);

    this.updateUserFactoryRelation(relation);

    return relation;
  }

  @Action({ rawError: true })
  async deleteUserFactoryRelation(
    relation: UserFactoryRelation
  ): Promise<void> {
    await DIContainer.UserFactoryRelationRepository.deleteById(relation.id);
    this.deleteUserFactoryRelationFromStore(relation);
  }

  // ################################### FACTORY INVOICING SETTINGS #########################################

  @Mutation
  updateFactoryInvoicingSettings(
    factoryInvoicingSettings: FactoryInvoicingSettings
  ): void {
    Vue.set(
      this.factoryInvoicingSettings,
      factoryInvoicingSettings.id,
      factoryInvoicingSettings
    );
  }

  @Action({ rawError: true })
  async saveFactoryInvoicingSettings(
    factoryInvoicingSettings: FactoryInvoicingSettings
  ): Promise<void> {
    await DIContainer.FactoryInvoicingSettingsRepository.save(
      factoryInvoicingSettings
    );

    this.updateFactoryInvoicingSettings(factoryInvoicingSettings);
  }

  @Action({ rawError: true })
  async fetchFactoryInvoicingSettingsById({
    factoryId,
    useCache = true,
  }: {
    factoryId: string;
    useCache?: boolean;
  }): Promise<FactoryInvoicingSettings | undefined> {
    let factoryInvoicingSettings: FactoryInvoicingSettings | undefined = this
      .factoryInvoicingSettings[factoryId];

    if (useCache && factoryInvoicingSettings) {
      return factoryInvoicingSettings;
    }

    factoryInvoicingSettings = await DIContainer.FactoryInvoicingSettingsRepository.fetchByFactoryId(
      factoryId
    );

    if (!factoryInvoicingSettings) {
      return;
    }

    this.updateFactoryInvoicingSettings(factoryInvoicingSettings);
  }

  // ################################### EVENTS #########################################

  @Action({ rawError: true })
  async onPlushieUpdated(plushieId: string): Promise<void> {
    if (!this.plushie[plushieId]) {
      return;
    }

    if (!this.plushie[plushieId].factoryAssignment) {
      return;
    }

    const assignment = await this.loadFactoryAssignmentByPlushieId({
      plushieId,
      useCache: false,
    });

    if (!assignment) {
      return;
    }

    if (!this.factory[assignment.factory]) {
      await this.loadFactoryById({ id: assignment.factory });
    }
  }

  // ################################### DATA WIPING #########################################

  @Mutation
  resetState(): void {
    phaseSupportRequestGeneration++;
    const state = (dataStore.state as any)[name];

    if (state) {
      Object.assign(state, getDefaultState());
    }
  }
}
